import AddIcon from "@mui/icons-material/Add";
import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, styled, type SxProps, type Theme } from "~/common/mui";
import { ControlField } from "./ControlField";
import { TileOption, TileOptionButton } from "./TileOption";
import type { TileSelectControlViewModel } from "./controlViewModel";

export interface TileSelectControlProps extends Omit<TileSelectControlViewModel, "type" | "controlIndex"> {
  onChange: (value: string) => void;
  className?: string;
  sx?: SxProps<Theme>;
}

const OptionsGrid = styled(Box, {
  shouldForwardProp: (prop) => prop !== "layout" && prop !== "columnCount",
})<{ layout: "fill" | "compact"; columnCount: number }>(({ layout, columnCount, theme }) => ({
  "--tile-min-width": theme.spacing(24),
  display: "grid",
  gridTemplateColumns:
    layout === "compact"
      ? `repeat(${columnCount}, minmax(var(--tile-min-width), ${theme.spacing(33)}))`
      : `repeat(auto-fit, minmax(${theme.spacing(24)}, 1fr))`,
  gap: theme.spacing(2),
  alignItems: "stretch",
  ...(layout === "fill"
    ? {
        [`@container (min-width: ${theme.spacing(128)})`]: {
          gap: theme.spacing(3),
        },
      }
    : undefined),
}));

const OptionsContainer = styled(Box)({
  containerType: "inline-size",
});

const ShowMoreButton = styled(TileOptionButton)(({ theme }) => ({
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1),
  textAlign: "center",
}));

const getRenderedColumnCount = (element: HTMLElement) => {
  const style = getComputedStyle(element);
  const minimumTileWidth = Number.parseFloat(style.getPropertyValue("--tile-min-width"));
  const columnGap = Number.parseFloat(style.columnGap);
  return Math.max(Math.floor((element.clientWidth + columnGap) / (minimumTileWidth + columnGap)), 1);
};

export const TileSelectControl = ({
  label,
  hideLabel,
  info,
  helperText,
  error,
  errorText,
  disabled = false,
  value,
  options,
  layout = "fill",
  standardLabel,
  onChange,
  className,
  sx,
}: TileSelectControlProps) => {
  const { t } = useTranslation();
  const gridRef = useRef<HTMLDivElement>(null);
  const [columnCount, setColumnCount] = useState(1);
  const [expanded, setExpanded] = useState(false);

  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid) {
      return;
    }

    const updateColumnCount = () => setColumnCount(getRenderedColumnCount(grid));
    updateColumnCount();

    const observer = new ResizeObserver(updateColumnCount);
    observer.observe(grid);
    return () => observer.disconnect();
  }, []);

  const collapsedCapacity = columnCount * 2;
  const hasOverflow = options.length > collapsedCapacity;
  const visibleOptions = !expanded && hasOverflow ? options.slice(0, collapsedCapacity - 1) : options;
  const hiddenOptionCount = options.length - visibleOptions.length;

  return (
    <ControlField
      label={label}
      hideLabel={hideLabel}
      info={info}
      helperText={helperText}
      error={Boolean(error)}
      errorText={errorText}
      className={className}
      sx={sx}
    >
      <OptionsContainer>
        <OptionsGrid ref={gridRef} role="radiogroup" aria-label={label} layout={layout} columnCount={columnCount}>
          {visibleOptions.map((option) => {
            const selected = option.value === value;
            return (
              <TileOption
                key={option.value}
                option={option}
                selected={selected}
                hasError={Boolean(error) && value === null}
                disabled={disabled}
                layout={layout}
                standardLabel={standardLabel}
                onSelect={onChange}
              />
            );
          })}
          {!expanded && hasOverflow ? (
            <ShowMoreButton
              selected={false}
              hasError={false}
              onClick={() => setExpanded(true)}
              aria-label={t("feature.configurator.v2.tiles.showMore", {
                count: hiddenOptionCount,
                defaultValue: "Show {{count}} more",
              })}
            >
              <AddIcon aria-hidden />
              <Typography variant="titleMedium" sx={{ fontWeight: 700 }}>
                {t("feature.configurator.v2.tiles.showMore", {
                  count: hiddenOptionCount,
                  defaultValue: "Show {{count}} more",
                })}
              </Typography>
            </ShowMoreButton>
          ) : null}
        </OptionsGrid>
      </OptionsContainer>
    </ControlField>
  );
};
